import type { ReactNode } from 'react'
import { Wordmark } from './Wordmark'

/**
 * The top of every screen: the platform's mark, the word Admin, and whatever the screen puts
 * beside them.
 *
 * The wordmark and Admin are one heading rather than a mark beside a title, so the page
 * announces itself as "EF Admin" in one go. It is a component rather than markup
 * in the shell because the two states before the shell exists -- reading the configuration,
 * and failing to -- are still this product and should still look like it.
 */
export function Masthead({
  beside,
  nav,
  children,
}: {
  /** What sits beside the mark: the Exam switcher, once there is an Exam to switch. */
  beside?: ReactNode
  nav?: ReactNode
  children?: ReactNode
}) {
  return (
    <header className="border-b border-ink-100">
      {/* A three-column grid, not a flex row, and for the same reason Navbar.astro is one: the
          areas centre on the row rather than on whatever space is left over. Equal outer columns
          (1fr, not auto) so the centre is the container's centre exactly: with auto sides the
          middle column sits off by half the difference between a wide mark and two narrow icons.

          69px tall, and every part of that is deliberate: py-4 is the App's own masthead padding,
          and both outer columns are trimmed to 36px -- the mark stack by taking the line box off
          the badge, the icon buttons by p-1.5. Either one left at its natural size floors the row
          at 40px, so they only come down together. The App's masthead reaches the same 69px a
          different way -- a min-height rather than padding, because its lockup is sized by a
          clamp and would not follow padding alone. */}
      <div className="mx-auto grid max-w-page grid-cols-[1fr_auto_1fr] items-center gap-x-8 px-(--container-gutter) py-4">
        {/* Admin sits ABOVE the mark as a small badge, the visual clue that this is the staff
            tool and not the App. flex-col-reverse rather than putting the badge first in the
            markup: reading order decides the heading's accessible name, and that must stay
            "EF Admin" rather than becoming "Admin EF".

            No gap, no vertical padding, and leading-none on the badge: uppercase at 12px has room
            to spare inside a 12px line box, so the two words sit tight and the stack is 36px --
            the wordmark's 24 and the badge's 12, and nothing else. */}
        {/* Named explicitly: stacked, the mark and the badge are two flex children with no text
            between them, so the computed name would run together as "EFAdmin". */}
        {/* Last baseline, not centre: the mark carries the Admin badge above it and the Exam's
            code carries the area nav's 2px underline and its padding below, so the two boxes
            are neither the same height nor hung the same way. Centred as boxes, that furniture
            lifts the Exam's code above the mark. Aligning the last baselines lines the two
            words up on the row instead, and keeps them there at every width -- --text-nav is a
            clamp, so any fixed nudge would be right at one viewport and wrong at the rest. */}
        <div className="flex items-baseline-last gap-1.5 justify-self-start">
          <h1
            aria-label="EF Admin"
            className="flex flex-col-reverse items-center text-2xl leading-none"
          >
            <Wordmark />
            <span className="rounded-full bg-green-050 px-2 font-text text-xs leading-none font-bold tracking-widest text-green-800 uppercase">
              Admin
            </span>
          </h1>
          {beside}
        </div>
        <div>{nav}</div>
        {/* The icons are one group: when the row wraps on a narrow screen they go together,
            rather than the account dropping under the wordmark on its own. */}
        <div className="flex items-center justify-self-end gap-1">{children}</div>
      </div>
    </header>
  )
}
